export type DrawerMode = 'earning' | 'deduction';
export type PayFrequency = 'weekly' | 'biweekly' | 'semimonthly' | 'monthly';

export interface LineInfo {
  kind: 'fixed' | 'estimated' | 'variable';
  label: string;
  amount: number | null;
  standard: boolean;
  rate: number;
}

export function standardHoursForFrequency(freq: PayFrequency): number {
  if (freq === 'weekly') return 40;
  if (freq === 'biweekly') return 80;
  if (freq === 'semimonthly') return 86.67;
  return 173.33;
}

export function prorateMonthlyToFrequency(monthlyAmount: number, freq: PayFrequency): number {
  const m = Number(monthlyAmount) || 0;
  if (freq === 'weekly') return (m * 12) / 52;
  if (freq === 'biweekly') return (m * 12) / 26;
  if (freq === 'semimonthly') return m / 2;
  return m;
}

export function parseUnitPeriod(unit: string | undefined): string | null {
  if (!unit) return null;
  const u = String(unit).toLowerCase().trim();
  if (u.indexOf('month') !== -1) return 'month';
  if (u.indexOf('year') !== -1) return 'year';
  if (u.indexOf('hr') !== -1 || u.indexOf('hour') !== -1) return 'hour';
  if (u.indexOf('km') !== -1) return 'km';
  if (u.indexOf('day') !== -1) return 'day';
  if (u.indexOf('visit') !== -1) return 'visit';
  return null;
}

export function getEarningLine(item: any, freq: PayFrequency): LineInfo {
  const pt = item.pay_type || {};
  const rate = item.rate_override != null ? Number(item.rate_override) : Number(pt.default_rate || 0);
  const unit = item.unit_label_override || pt.unit_label || '';
  const period = parseUnitPeriod(unit);
  const calcMethod = pt.calc_method || 'fixed';

  if (calcMethod === 'percent_gross') {
    return { kind: 'variable', label: rate.toFixed(2) + '% of gross', amount: null, standard: false, rate };
  }
  if (period === 'month') {
    return { kind: 'fixed', label: '$' + rate.toFixed(2) + ' /month', amount: prorateMonthlyToFrequency(rate, freq), standard: false, rate };
  }
  if (period === 'year') {
    const monthlyFromYear = rate / 12;
    return { kind: 'fixed', label: '$' + rate.toFixed(2) + ' /year', amount: prorateMonthlyToFrequency(monthlyFromYear, freq), standard: false, rate };
  }
  if (period === 'hour') {
    const stdHours = standardHoursForFrequency(freq);
    const isOvertime = (pt.name || '').toLowerCase().indexOf('overtime') !== -1;
    const hoursForStandard = isOvertime ? 0 : stdHours;
    return { kind: 'estimated', label: '$' + rate.toFixed(2) + ' /hr x ' + hoursForStandard + ' hrs', amount: rate * hoursForStandard, standard: true, rate };
  }
  if (period === 'km') {
    return { kind: 'variable', label: '$' + rate.toFixed(2) + ' /km x distance driven', amount: null, standard: false, rate };
  }
  if (period === 'visit') {
    return { kind: 'variable', label: '$' + rate.toFixed(2) + ' /visit x visits', amount: null, standard: false, rate };
  }
  return { kind: 'variable', label: 'applied when triggered', amount: null, standard: false, rate };
}

export function getDeductionLine(item: any, freq: PayFrequency, estimatedGross: number): LineInfo {
  const dt = item.deduction_type || {};
  const amount = item.amount_override != null ? Number(item.amount_override) : Number(dt.default_amount || 0);
  const unit = dt.unit_label || '';
  const period = parseUnitPeriod(unit);
  const calcMethod = dt.calc_method || 'fixed';

  if (calcMethod === 'percent_gross') {
    return { kind: 'estimated', label: amount.toFixed(2) + '% x estimated gross', amount: (estimatedGross * amount) / 100, standard: true, rate: amount };
  }
  if (period === 'month') {
    return { kind: 'fixed', label: '$' + amount.toFixed(2) + ' /month', amount: prorateMonthlyToFrequency(amount, freq), standard: false, rate: amount };
  }
  if (period === 'year') {
    return { kind: 'fixed', label: '$' + amount.toFixed(2) + ' /year', amount: prorateMonthlyToFrequency(amount / 12, freq), standard: false, rate: amount };
  }
  return { kind: 'variable', label: 'applied when triggered', amount: null, standard: false, rate: amount };
}

export function fmt(n: number): string {
  return Number(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}